import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserReviewsResponse } from '../types';

interface UserReviewsCardProps {
  reviews: UserReviewsResponse | null;
  isMobile?: boolean;
  isPublicProfile?: boolean;
  fixedDesktopHeight?: number;
}

export const UserReviewsCard: React.FC<UserReviewsCardProps> = ({
  reviews,
  isMobile = false,
  isPublicProfile = false,
  fixedDesktopHeight
}) => {
  const navigate = useNavigate();
  const hasFixedDesktopHeight = !isMobile && typeof fixedDesktopHeight === 'number';

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div style={{
      backgroundColor: '#23272A',
      padding: isMobile ? '10px' : '24px', // Padding más compacto en mobile
      borderRadius: '18px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      border: '1px solid #33383B',
      flex: isMobile ? 'none' : '1',
      width: '100%',
      boxSizing: 'border-box',
      minHeight: hasFixedDesktopHeight ? `${fixedDesktopHeight}px` : (isMobile ? 'auto' : '300px'),
      height: hasFixedDesktopHeight ? `${fixedDesktopHeight}px` : 'auto',
      maxHeight: hasFixedDesktopHeight ? `${fixedDesktopHeight}px` : 'none',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <h3 style={{ 
        fontSize: isMobile ? '16px' : '18px', 
        fontWeight: 'bold', 
        margin: '0 0 12px 0', // Margen más compacto en mobile 
        color: '#FFFFFF',
        textAlign: 'center'
      }}>
        ⭐ Mis Últimas Reviews
      </h3>
      
      {reviews && reviews.matchesWithReviews && reviews.matchesWithReviews.length > 0 ? (
        <>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '8px' : '16px',
            flex: 1,
            minHeight: 0,
            overflowY: hasFixedDesktopHeight ? 'auto' : 'visible',
            paddingRight: hasFixedDesktopHeight ? '4px' : 0
          }}> {/* Gap más compacto en mobile */}
            {reviews.matchesWithReviews.slice(0, 3).map((matchWithReviews) => (
              <div key={matchWithReviews.match.id} style={{
                backgroundColor: '#2C2F33',
                borderRadius: '8px',
                                padding: isMobile ? '8px' : '16px', // Padding más compacto en mobile
                border: '1px solid #40444B'
              }}>
                <div style={{ marginBottom: isMobile ? '6px' : '12px' }}> {/* Margen más compacto en mobile */}
                  <h4 style={{ margin: '0 0 4px 0', color: '#00E676', fontSize: '14px' }}>
                    {formatDate(matchWithReviews.match.date)}
                  </h4>
                  <p style={{ margin: 0, color: '#B0BEC5', fontSize: '12px' }}>
                    {matchWithReviews.match.location}
                  </p>
                </div>
                
                {isPublicProfile || matchWithReviews.canViewReviews ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '4px' : '10px' }}> {/* Gap más compacto en mobile */}
                    {matchWithReviews.reviews.slice(0, 2).map((review) => (
                      <div key={review.id} style={{
                        backgroundColor: '#40444B',
                        borderRadius: '6px',
                                                padding: isMobile ? '6px' : '12px', // Padding más compacto en mobile
                        border: '1px solid #555'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ color: '#B0BEC5', fontSize: '11px' }}>
                            {(() => {
                              const date = new Date(review.createdAt);
                              const day = date.getDate().toString().padStart(2, '0');
                              const month = (date.getMonth() + 1).toString().padStart(2, '0');
                              return `${day}/${month}/${date.getFullYear()}`;
                            })()}
                          </span>
                          <span style={{ color: '#B0BEC5', fontSize: '11px' }}>
                            {(() => {
                              const date = new Date(review.createdAt);
                              const day = date.getDate().toString().padStart(2, '0');
                              const month = (date.getMonth() + 1).toString().padStart(2, '0');
                              const hours = date.getHours().toString().padStart(2, '0');
                              const minutes = date.getMinutes().toString().padStart(2, '0');
                              return `${day}/${month} ${hours}:${minutes}`;
                            })()}
                          </span>
                        </div>
                        <p style={{ margin: '0 0 6px 0', color: '#FFFFFF', fontSize: '12px' }}>{review.comment}</p>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                          {/* Tags predefinidos positivos */}
                          {review.positiveTags.slice(0, 2).map((tag) => (
                            <span key={tag} style={{
                              backgroundColor: '#00E676',
                              color: '#181A1B',
                              padding: '1px 4px',
                              borderRadius: '4px',
                              fontSize: '9px',
                              fontWeight: 'bold'
                            }}>
                              {tag}
                            </span>
                          ))}
                          {/* Tags predefinidos negativos */}
                          {review.negativeTags.slice(0, 1).map((tag) => (
                            <span key={tag} style={{
                              backgroundColor: '#FF6B6B',
                              color: '#FFFFFF',
                              padding: '1px 4px',
                              borderRadius: '4px',
                              fontSize: '9px',
                              fontWeight: 'bold'
                            }}>
                              {tag}
                            </span>
                          ))}
                          {/* Tags personalizados positivos */}
                          {review.customPositiveTags && review.customPositiveTags.slice(0, 1).map((tag) => (
                            <span key={tag} style={{
                              backgroundColor: '#00BFA5',
                              color: '#FFFFFF',
                              padding: '1px 4px',
                              borderRadius: '4px',
                              fontSize: '9px',
                              fontWeight: 'bold',
                              border: '1px solid #FFFFFF'
                            }}>
                              {tag}
                            </span>
                          ))}
                          {/* Tags personalizados negativos */}
                          {review.customNegativeTags && review.customNegativeTags.slice(0, 1).map((tag) => (
                            <span key={tag} style={{
                              backgroundColor: '#D32F2F',
                              color: '#FFFFFF',
                              padding: '1px 4px',
                              borderRadius: '4px',
                              fontSize: '9px',
                              fontWeight: 'bold',
                              border: '1px solid #FFFFFF'
                            }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#B0BEC5' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>👀</div>
                    <p style={{ margin: 0, fontSize: '12px' }}>
                      Te calificaron {Math.max(1, matchWithReviews.reviewsReceived)} {Math.max(1, matchWithReviews.reviewsReceived) === 1 ? 'vez' : 'veces'} en este partido
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '10px' }}>
                      Dejá tu reseña y descubrí qué te pusieron tus compañeros
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div style={{ marginTop: isMobile ? '8px' : '10px', flexShrink: 0 }}>
            <button
              onClick={() => navigate('/reviews')}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#FFD600',
                border: 'none',
                borderRadius: '8px',
                color: '#181A1B',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#FFC107';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFD600';
              }}
            >
              📋 Ver Todas Mis Reviews
            </button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#B0BEC5' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⭐</div>
          <div style={{ fontSize: '14px', marginBottom: '8px' }}>No tienes reviews aún</div>
          <div style={{ fontSize: '12px' }}>Las reviews aparecerán aquí después de que tus compañeros te evalúen</div>
        </div>
      )}
    </div>
  );
}; 